import { useState } from "react";
import { ChevronDown, Eye, FileSignature, FileText, Upload } from "lucide-react";
import { fmtDate, fmtCurrency } from "@/lib/format";
import { DocViewer } from "@/components/DocViewer";
import { SinalCheck } from "@/components/DeedChecks";
import { gpTrata } from "@/components/gpView";

const Fact = ({ k, v, src, testid }) => (
  <div data-testid={testid}>
    <div className="text-xs text-slate-500">{k}</div>
    <div className="text-[15px] font-medium text-ec-green">{v}</div>
    {src && <div className="text-[11px] text-slate-400">{src}</div>}
  </div>
);

const CMI_TXT = {
  completo: ["Pronto a gerar", "amber"], gerado: ["Gerado · falta enviar para assinatura", "amber"], enviado: ["Enviado para assinatura", "sky"],
  lembrar: ["Enviado · ainda sem assinatura", "amber"], assinado: ["Assinado", "green"],
};
const TONE = { amber: "bg-amber-50 text-amber-800", sky: "bg-sky-50 text-sky-800", green: "bg-green-50 text-green-800", slate: "bg-slate-100 text-slate-600" };
const btn = "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-semibold transition-colors";
const primary = `${btn} bg-ec-green text-white hover:bg-ec-greenDark`;
const outline = `${btn} border border-slate-300 bg-white navy-text hover:bg-slate-50`;

function Shell({ title, state, tone, detail, facts = null, id, children }) {
  return (
    <section id={id} className="bg-white rounded-[18px] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-[18px] md:p-6 scroll-mt-24 transition-shadow" data-testid="focus-doc">
      <div className="text-xs font-bold tracking-wider text-slate-500 uppercase">Documento em foco</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <FileText size={20} className="text-ec-green shrink-0" />
        <h2 className="font-heading text-lg md:text-xl navy-text">{title}</h2>
        {state && <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${TONE[tone]}`} data-testid="focus-doc-state">{state}</span>}
      </div>
      {detail && <p className="text-sm text-slate-500 mt-1" data-testid="focus-doc-detail">{detail}</p>}
      {facts}
      <div className="mt-3 flex flex-wrap items-center gap-2">{children}</div>
    </section>
  );
}

// O documento-chave da fase: CMI na angariação, CPCV na venda.
export function FocusDoc({ p, ro, onPanel, onFocus, onDone }) {
  const [view, setView] = useState(false);
  const [more, setMore] = useState(false);
  if (["escriturado", "sem_venda"].includes(p.status)) return null;
  if (p.stage === "angariacao") {
    const [state, tone] = CMI_TXT[p.cmi_state] || ["Faltam dados para gerar", "slate"];
    return (
      <Shell title="Contrato de mediação (CMI)" state={state} tone={tone}>
        <button onClick={() => onPanel("cmi")} className={p.cmi_state === "gerado" && !ro ? outline : primary} data-testid="focus-doc-cmi-open"><Eye size={16} /> Ver CMI</button>
        {!ro && p.cmi_state === "gerado" && <button onClick={() => onPanel("cmi-sign")} className={primary} data-testid="focus-doc-cmi-sign"><FileSignature size={16} /> Enviar para assinatura</button>}
      </Shell>
    );
  }
  const doc = (p.documents || []).find((d) => d.key === "cpcv_assinado");
  if (!doc) return null;
  const has = !!doc.file_id;
  const facts = more && (
    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2" data-testid="negocio">
      <Fact k="Sinal" v={fmtCurrency(p.deposit)} />
      <Fact k="CPCV" v={fmtDate(p.cpcv?.date)} src={p.cpcv?.date_source} testid="kv-cpcv-date" />
      <Fact k="Limite do CPCV" v={fmtDate(p.cpcv?.limit || p.cpcv_deadline)} src={p.cpcv?.limit_source} testid="kv-cpcv-limit" />
      {!gpTrata(p) && <Fact k="Financiamento" v={p.has_credit ? "Com crédito" : "Sem crédito"} />}
      {!gpTrata(p) && <Fact k="Hipoteca do proprietário" v={p.has_mortgage ? "Sim" : "Não"} />}
      {!gpTrata(p) && <div className="col-span-2 md:col-span-4"><SinalCheck p={p} onDone={onDone} /></div>}
    </div>
  );
  return (
    <Shell id="sec-cpcv" title="CPCV" state={!has || ["cpcv_draft", "cpcv_unsigned"].includes(doc.alert?.code) ? null : doc.verdict === "validado" ? "Verificado" : "Carregado"} tone={has ? "green" : "amber"} facts={facts}>
      {has && <button onClick={() => setView(true)} className={primary} data-testid="focus-doc-view"><Eye size={16} /> Ver documento</button>}
      {!has && !ro && <button onClick={() => onFocus(doc.key)} className={primary} data-testid="focus-doc-upload"><Upload size={16} /> Carregar CPCV assinado</button>}
      {view && <DocViewer fileId={doc.file_id} title="CPCV" onClose={() => setView(false)} />}
      <button onClick={() => setMore(!more)} className="inline-flex items-center gap-1 h-9 px-2 text-sm font-medium text-slate-500 hover:text-ec-green" data-testid="focus-doc-details">
        {more ? "Esconder detalhes" : "Sinal e datas"} <ChevronDown size={14} className={`transition-transform ${more ? "rotate-180" : ""}`} />
      </button>
    </Shell>
  );
}
